/**
 * Budget Templates API (issue #98)
 *
 * Templates live at /api/budget-module/templates/, NOT under /settings/ like
 * clients and suppliers, because the router registers them on the main budget
 * router.
 *
 * The list endpoint returns a lightweight row (name only); the detail endpoint
 * returns the full template with positions reconciled against current settings
 * and billing rates resolved against the template's overrides.
 */

import { HttpClient } from './service'
import { IPagination } from '../types/pagination'

/** A row in the Templates settings list. Schema: BudgetTemplateListSerializer */
export interface BudgetTemplateListItem {
  id: number
  name: string
  created_at: string
  updated_at: string
}

/**
 * Schema: /shared/schemas/budget_schemas.json#/definitions/billingRate
 * `is_override` is present on template rates only, never on a budget's snapshot.
 */
export interface BudgetTemplateBillingRate {
  id: number
  department_id: number
  department_name: string
  department_shortcut: string
  position_id: number
  position_name: string
  position_shortcut: string
  billing_rate: number
  start_date: string | null
  end_date: string | null
  is_override: boolean
}

/** A full template. Schema: BudgetTemplateSerializer */
export interface BudgetTemplate {
  id: number
  name: string
  budget_positions: any[]
  external_positions: any[]
  billing_rates_data: BudgetTemplateBillingRate[]
  created_at: string
  updated_at: string
}

/** The unsaved skeleton the Add Template modal opens on. */
export interface BudgetTemplateDefaults {
  budget_positions: any[]
  external_positions: any[]
  billing_rates_data: BudgetTemplateBillingRate[]
}

/** What create/update accept. Omitting billing_rates_data leaves overrides untouched. */
export interface BudgetTemplatePayload {
  name: string
  budget_positions?: any[]
  external_positions?: any[]
  billing_rates_data?: BudgetTemplateBillingRate[]
}

class BudgetTemplatesApi {
  private basePath = '/api/budget-module/templates'

  /**
   * Get a paginated, searchable page of templates for the settings list.
   * @param params - page, page_size (max 20), search, order ('name' or '-name')
   */
  async getPaginatedTemplates(params: {
    page?: number
    page_size?: number
    search?: string
    order?: string
  }): Promise<IPagination<BudgetTemplateListItem[]>> {
    const queryParams = new URLSearchParams()
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params.search) queryParams.append('search', params.search)
    if (params.order) queryParams.append('order', params.order)

    const url = queryParams.toString()
      ? `${this.basePath}/?${queryParams.toString()}`
      : `${this.basePath}/`

    return HttpClient.get<IPagination<BudgetTemplateListItem[]>, null>(url)
  }

  /** Get every template unpaginated, for the budget wizard's template dropdown. */
  async getAllTemplates(): Promise<BudgetTemplateListItem[]> {
    return HttpClient.get<BudgetTemplateListItem[], null>(`${this.basePath}/all/`)
  }

  /** Get one template, reconciled against current settings and resolved rates. */
  async getTemplate(id: number): Promise<BudgetTemplate> {
    return HttpClient.get<BudgetTemplate, null>(`${this.basePath}/${id}/`)
  }

  /** Get the unsaved skeleton for a new template: all positions off, live rates. */
  async getDefaults(): Promise<BudgetTemplateDefaults> {
    return HttpClient.get<BudgetTemplateDefaults, null>(`${this.basePath}/defaults/`)
  }

  async createTemplate(data: BudgetTemplatePayload): Promise<BudgetTemplate> {
    return HttpClient.post<BudgetTemplate, BudgetTemplatePayload>(`${this.basePath}/`, data)
  }

  async updateTemplate(id: number, data: Partial<BudgetTemplatePayload>): Promise<BudgetTemplate> {
    return HttpClient.patch<BudgetTemplate, Partial<BudgetTemplatePayload>>(`${this.basePath}/`, id, data)
  }

  async deleteTemplate(id: number): Promise<void> {
    return HttpClient.delete<void, void>(`${this.basePath}/`, id)
  }
}

export default new BudgetTemplatesApi()
